import { createFileRoute, useRouter } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  ArrowRightLeft,
  Beef,
  Check,
  CheckCheck,
  ChevronDown,
  Copy,
  Download,
  Droplets,
  Eye,
  EyeOff,
  Flame,
  MoreHorizontal,
  PlusIcon,
  Trash2,
  Upload,
  Utensils,
  Link2,
  NotebookText,
  Wheat,
  History,
} from 'lucide-react'
import {
  FunctionComponent,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react'
import { errorToast } from '@/lib/utils/ToastUtils'
import { trpc } from '@/lib/trpc/client'
import { trpcUtils } from '@/lib/trpc/vanilla'
import {
  patchKeyedListCache,
  patchListCache,
  prependToList,
} from '@trainrr/api-client'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase/supabaseClient'
import { Meal } from '@/components/nutrition/mealColumns'
import { useOrg } from '@/lib/states/session/hooks'
import { Food } from '@/components/nutrition/foodColumns'
import MealInfoTable from '@/components/nutrition/mealInfoTable'
import MealImage from '@/components/nutrition/MealImage'
import VersionHistorySheet, {
  PlanHistoryButton,
  PlanVersionTag,
} from '@/components/planHistory/VersionHistorySheet'
import {
  useVersionHistoryOpen,
  useVersionHistoryPreview,
} from '@/components/planHistory/VersionHistoryHost'
import {
  mealEditorStateFromSnapshot,
  type MealRevisionSnapshot,
} from '@/lib/planHistory/hydrateRevision'
import { liveMealSnapshotFromEditor } from '@/lib/planHistory/liveSnapshot'
import { useEditorHistory } from '@/lib/hooks/useEditorHistory'
import type { EditorCommitOptions } from '@/lib/hooks/useEditorHistory'
import { toMealEditorDocument } from '@/lib/editorHistory/documents'
import { EditorUndoControls } from '@/components/planHistory/EditorUndoControls'
import { duplicateMealPlan } from '@/lib/nutrition/duplicateMealPlan'
import {
  hasLocalUnsavedDraft,
  resolveSelectedPlan,
} from '@/lib/plans/selectedPlan'
import {
  buildMealImagePath,
  FoodsFieldTotal,
  MEAL_IMAGE_BUCKET,
  MEAL_IMAGE_MAX_BYTES,
} from '@/lib/utils/MealUtils'
import {
  applySavedTarget,
  savedTargetToRow,
  type NutritionTargetRow,
} from '@/lib/nutrition/targetCache'
import { averageDays } from '@/lib/nutrition/targetMacros'
import { MACRO_COLORS } from '@/lib/nutrition/macroColors'
import { MealPlanMacroBars } from '@/components/nutrition/MealPlanMacroBars'
import LoadingSpinner from '@/components/LoadingSpinner'
import { ClientNutritionLoadingState } from '@/components/client/ClientTabLoadingStates'
import { v4 as uuidv4 } from 'uuid'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils/ui'
import { useMainScrollableElement } from '@/lib/hooks/useMainScrollableElement'
import FoodSelectDialog from '@/components/nutrition/foodSelectDialog'
import { ButtonGroup, ButtonGroupSeparator } from '@/components/ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import InputFieldEditButton from '@/components/inputFieldEditButton'
import PlanContexMenu, { PlanChangeAlert } from '@/components/PlanContextMenu'
import AlertBlocker from '@/components/AlertBlocker'
import DeleteAlert from '@/components/DeleteAlert'
import MealDialog from '@/components/nutrition/mealDialog'
import { Textarea } from '@/components/ui/textarea'
import DuplicateMealPlanDialog from '@/components/nutrition/DuplicateMealPlanDialog'
import MealSelectDialog from '@/components/nutrition/MealSelectDialog'
import UnlinkTemplateDialog from '@/components/templates/UnlinkTemplateDialog'
import {
  MealPlanTemplateLinkDialog,
  type MealPlanTemplateSummary,
} from '@/components/templates/MealPlanTemplateLinkDialog'
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  MeasuringStrategy,
  Active,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, arrayMove, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ClientNutritionLogs } from '@/components/client/logs/ClientNutritionLogs'
import { ClientNutritionTargets } from '@/components/client/ClientNutritionTargets'
import { NutritionTargetDialog } from '@/components/client/NutritionTargetDialog'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useDropzone } from 'react-dropzone'
import * as luxon from 'luxon'

export interface MealPlan {
  is_visible: boolean
  is_primary: boolean
  id: string
  name: string
  is_template?: boolean
  meals: Meal[]
}

type TemplateMealPlan = MealPlan & {
  org_id: string | null
}

type SavedTemplate = {
  id: string
  name: string
  org_id: string | null
  meals: Meal[]
}

type AssignableTemplateRow = SavedTemplate & { is_template: boolean }

const sortMealPlans = (plans: MealPlan[]) =>
  [...plans]
    .map((plan) => ({
      ...plan,
      meals: [...(plan.meals ?? [])].sort(
        (a, b) => (a.meal_number ?? 0) - (b.meal_number ?? 0),
      ),
    }))
    .sort((a, b) => Number(b.is_primary) - Number(a.is_primary))

export const Route = createFileRoute(
  '/_auth/_dashboard/clients/$clientId/_layout/nutrition',
)({
  component: ClientNutrition,
  loader: ({ params }) =>
    trpcUtils.nutrition.listForClient.ensureData({
      clientId: params.clientId,
    }),
  pendingComponent: ClientNutritionLoadingState,
})

function ClientNutrition() {
  const { clientId } = Route.useParams()
  const { data: plans, isPending: isPlansPending } =
    trpc.nutrition.listForClient.useQuery(
      { clientId },
      { enabled: Boolean(clientId) },
    )
  const sortedPlans = useMemo(
    () => sortMealPlans(plans ?? []),
    [plans],
  )
  if (isPlansPending || plans === undefined) return <ClientNutritionLoadingState />
  return (
    <ClientNutritionEditor
      key={clientId}
      loadedMealPlans={sortedPlans}
    />
  )
}

function ClientNutritionEditor({
  loadedMealPlans,
}: {
  loadedMealPlans: MealPlan[]
}) {
  const { clientId } = Route.useParams()

  const router = useRouter()
  const org = useOrg()

  const [mealPlans, setMealPlans] = useState<MealPlan[]>(loadedMealPlans)
  const [mealPlan, setMealPlan] = useState<MealPlan | null>(
    mealPlans?.[0] ?? null,
  )
  const mealPlanIdRef = useRef(mealPlan?.id)
  mealPlanIdRef.current = mealPlan?.id

  useEffect(() => {
    if (hasLocalUnsavedDraft(mealPlans, loadedMealPlans)) {
      return
    }

    setMealPlans(loadedMealPlans)
    const nextPlan = resolveSelectedPlan(loadedMealPlans, mealPlanIdRef.current)
    setMealPlan((current) => {
      if (!nextPlan) return null
      if (current?.id === nextPlan.id) {
        if (
          current.is_primary === nextPlan.is_primary &&
          current.is_visible === nextPlan.is_visible &&
          current.name === nextPlan.name
        ) {
          return current
        }
        return {
          ...current,
          is_primary: nextPlan.is_primary,
          is_visible: nextPlan.is_visible,
          name: nextPlan.name,
        }
      }
      return nextPlan
    })
  }, [loadedMealPlans])
  const [isCreatingMealPlan, setIsCreatingMealPlan] = useState(false)
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false)
  const [planAlertOptions, setplanAlertOptions] = useState<{
    id: string
    add: boolean
  }>()
  const [mealPlanDeleteAlert, setMealPlanDeleteAlert] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [targetHasChanges, setTargetHasChanges] = useState(false)
  const [nutritionTab, setNutritionTab] = useState('plan')
  const [pendingTab, setPendingTab] = useState<string | null>(null)
  const hasSavedMealPlans = (mealPlans ?? []).some((plan) => plan.id.length > 2)
  const linkedMealPlanIds = useMemo(
    () => new Set((mealPlans ?? []).map((plan: MealPlan) => plan.id)),
    [mealPlans],
  )

  const utils = trpc.useUtils()
  const deletePlan = trpc.nutrition.deletePlan.useMutation()
  const unlinkFromClient = trpc.nutrition.unlinkFromClient.useMutation()
  const assignToClient = trpc.nutrition.assignToClient.useMutation()
  const setClientPlanStatus = trpc.nutrition.setClientPlanStatus.useMutation()

  const { data: assignableTemplates } =
    trpc.nutrition.listAssignableTemplates.useQuery(undefined, {
      enabled: Boolean(org?.id),
    })
  const templateMealPlans = useMemo<TemplateMealPlan[]>(
    () =>
      (assignableTemplates ?? []).map((template) => ({
        ...template,
        is_primary: false,
        is_visible: true,
      })),
    [assignableTemplates],
  )

  // The local MealPlan shape omits fields the API rows carry (org_id, required
  // meal_number), so the cache utils are re-typed against it here.
  const patchClientPlansCache = useCallback(
    (updater: (plans: MealPlan[]) => MealPlan[]) => {
      patchKeyedListCache<MealPlan, { clientId: string }>(
        utils.nutrition.listForClient as never,
        { clientId },
        updater,
      )
    },
    [clientId, utils],
  )

  const handleTemplateSaved = useCallback(
    (template: SavedTemplate) => {
      patchListCache<AssignableTemplateRow>(
        utils.nutrition.listAssignableTemplates as never,
        (old) =>
          prependToList(old, {
            id: template.id,
            name: template.name,
            org_id: template.org_id,
            is_template: true,
            meals: template.meals,
          }),
      )
      patchListCache(utils.nutrition.listTemplates, (old) =>
        prependToList(old, {
          id: template.id,
          name: template.name,
          org_id: template.org_id,
          is_template: true,
          mealCount: template.meals.length,
          linkedClients: [],
        }),
      )
    },
    [utils],
  )

  const handleMealPlanSaved = (
    newPlan: MealPlan,
    options?: { invalidate?: boolean },
  ) => {
    const normalizedPlan: MealPlan = newPlan.is_visible
      ? newPlan
      : { ...newPlan, is_primary: false }

    let nextPlans = [...(mealPlans ?? [])]
    const index = nextPlans.findIndex((p) => p.id === mealPlan?.id)

    if (index >= 0) {
      nextPlans[index] = normalizedPlan
    } else {
      nextPlans.push(normalizedPlan)
    }

    if (normalizedPlan.is_primary) {
      nextPlans = nextPlans.map((p: MealPlan) =>
        p.id === normalizedPlan.id ? p : { ...p, is_primary: false },
      )
    }

    nextPlans = sortMealPlans(nextPlans)

    setMealPlans(nextPlans)
    setMealPlan(normalizedPlan)
    patchClientPlansCache(() => nextPlans)
    if (options?.invalidate !== false) {
      void router.invalidate()
    }
  }

  const handleMealPlanDuplicated = (newPlan: MealPlan) => {
    const normalizedPlan: MealPlan = newPlan.is_visible
      ? newPlan
      : { ...newPlan, is_primary: false }

    let nextPlans = [...(mealPlans ?? []), normalizedPlan]

    if (normalizedPlan.is_primary) {
      nextPlans = nextPlans.map((p: MealPlan) =>
        p.id === normalizedPlan.id ? p : { ...p, is_primary: false },
      )
    }

    nextPlans = sortMealPlans(nextPlans)

    setMealPlans(nextPlans)
    setMealPlan(normalizedPlan)
    patchClientPlansCache(() => nextPlans)
    void router.invalidate()
  }

  const handleLinkTemplate = async (template: MealPlanTemplateSummary) => {
    if (!template?.id) {
      return
    }
    const shouldPrimary =
      !hasSavedMealPlans ||
      !(mealPlans ?? []).some((plan: MealPlan) => plan.is_primary)
    let assignment
    try {
      assignment = await assignToClient.mutateAsync({
        clientId,
        planId: template.id,
        isPrimary: shouldPrimary,
        isVisible: true,
      })
    } catch (error) {
      console.error('Error linking meal plan template', error)
      errorToast(error instanceof Error ? error : null)
      return
    }
    const normalizedPlan: MealPlan = {
      ...template,
      meals: [...(template.meals ?? [])].sort(
        (a, b) => (a.meal_number ?? 0) - (b.meal_number ?? 0),
      ),
      is_template: template.is_template ?? true,
      is_primary: assignment.is_primary,
      is_visible: assignment.is_visible,
    }
    const nextPlans = sortMealPlans([
      ...(mealPlans ?? []).map((plan) =>
        normalizedPlan.is_primary ? { ...plan, is_primary: false } : plan,
      ),
      normalizedPlan,
    ])
    setMealPlans(nextPlans)
    setMealPlan(normalizedPlan)
    patchClientPlansCache(() => nextPlans)
    setTemplateDialogOpen(false)
    void router.invalidate()
    toast.success('Template linked to client')
  }

  const handlleDeleteMealPlan = async () => {
    setMealPlanDeleteAlert(false)
    const wasPrimary = mealPlan?.is_primary ?? false
    const isTemplatePlan = Boolean(mealPlan?.is_template)
    if (!mealPlan?.id || mealPlan?.id.length <= 2) {
      // If meal plan is not saved yet, remove it from mealPlans
      let newMeals = [...(mealPlans ?? [])]
      newMeals.splice(
        newMeals.findIndex((p) => p.id === mealPlan?.id),
        1,
      )
      if (wasPrimary && newMeals.length > 0) {
        newMeals = newMeals.map((plan: MealPlan, index: number) =>
          index === 0
            ? { ...plan, is_primary: true, is_visible: true }
            : { ...plan, is_primary: false },
        )
      }
      setMealPlans(newMeals)
      setMealPlan(newMeals?.[0] ?? null)
      setIsCreatingMealPlan(newMeals.length > 0)
      return
    }

    try {
      if (isTemplatePlan) {
        await unlinkFromClient.mutateAsync({ clientId, planId: mealPlan.id })
      } else {
        await deletePlan.mutateAsync({ planId: mealPlan.id })
      }
    } catch (error) {
      console.error(error)
      errorToast(error instanceof Error ? error : null)
      return
    }

    let newMeals = [...(mealPlans ?? [])]
    newMeals.splice(
      newMeals.findIndex((p) => p.id === mealPlan?.id),
      1,
    )
    if (wasPrimary && newMeals.length > 0) {
      try {
        await setClientPlanStatus.mutateAsync({
          clientId,
          planId: newMeals[0].id,
          isPrimary: true,
          isVisible: true,
        })
        newMeals = newMeals.map((plan: MealPlan, index: number) =>
          index === 0
            ? { ...plan, is_primary: true, is_visible: true }
            : { ...plan, is_primary: false },
        )
      } catch (error) {
        errorToast(error instanceof Error ? error : null)
      }
    }
    setMealPlans(newMeals)
    setMealPlan(newMeals?.[0] ?? null)
    patchClientPlansCache(() => newMeals)

    void router.invalidate()
    setIsCreatingMealPlan(false)
    toast.success(isTemplatePlan ? 'Meal Plan Removed' : 'Meal Plan Deleted')
  }

  const handleAddNewMealPlan = () => {
    setMealPlan(null)
    setIsCreatingMealPlan(true)
    const newMeals = [...(mealPlans ?? [])]
    const isFirstPlan = newMeals.length === 0
    const defaultMeal: Meal = {
      id: uuidv4(),
      name: 'New Meal',
      meal_number: 0,
      foods: [],
    }
    const newMeal = {
      is_visible: true,
      is_primary: isFirstPlan,
      name: 'New Meal Plan',
      meals: [defaultMeal],
      id: String(mealPlans?.length ?? '0'),
    }
    newMeals.push(newMeal)
    setMealPlan(newMeal)
    setMealPlans(newMeals)
  }

  const isLinkedPlan = Boolean(mealPlan?.is_template)

  const currentTabHasChanges =
    (nutritionTab === 'plan' && hasChanges) ||
    (nutritionTab === 'target' && targetHasChanges)

  const handleNutritionTabChange = (nextTab: string) => {
    if (nextTab === nutritionTab) return
    if (currentTabHasChanges) {
      setPendingTab(nextTab)
      return
    }
    setNutritionTab(nextTab)
  }

  return (
    <>
      <DeleteAlert
        title={`${isLinkedPlan ? 'Remove' : 'Delete'} ${
          mealPlan?.name ?? 'Meal Plan'
        }`}
        description={`Are you sure you want to ${
          isLinkedPlan ? 'remove' : 'delete'
        } ${mealPlan?.name ?? 'Meal Plan'}?`}
        confirmText={isLinkedPlan ? 'Remove' : 'Delete'}
        open={mealPlanDeleteAlert}
        onCancel={() => setMealPlanDeleteAlert(false)}
        onDelete={handlleDeleteMealPlan}
      />
      <AlertBlocker shouldBlock={hasChanges || targetHasChanges} />
      <PlanChangeAlert
        open={planAlertOptions !== undefined}
        onClose={(canContinue) => {
          if (canContinue) {
            if (planAlertOptions?.add) {
              handleAddNewMealPlan()
            } else {
              if (mealPlan?.id.length ?? 0 <= 2) {
                // Remove current meal plan from mealPlans
                const newMeals = [...(mealPlans ?? [])]
                newMeals.splice(
                  newMeals.findIndex((p) => p.id === mealPlan?.id),
                  1,
                )
                setMealPlans(newMeals)
              }

              const plan = mealPlans?.find((p) => p.id === planAlertOptions?.id)
              setMealPlan(plan ?? null)
            }
          }
          setplanAlertOptions(undefined)
        }}
      />
      <AlertDialog
        open={pendingTab !== null}
        onOpenChange={(open) => {
          if (!open) setPendingTab(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to leave?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingTab(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingTab) setNutritionTab(pendingTab)
                setPendingTab(null)
              }}
            >
              Leave
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <MealPlanTemplateLinkDialog
        open={templateDialogOpen}
        onOpenChange={setTemplateDialogOpen}
        templates={templateMealPlans ?? []}
        linkedPlanIds={linkedMealPlanIds}
        onLinkTemplate={handleLinkTemplate}
      />
      <Tabs value={nutritionTab} onValueChange={handleNutritionTabChange}>
        <div className="mx-auto mb-4 w-full max-w-screen-2xl border-b px-4 pb-2 md:w-[90%] lg:px-0">
          <TabsList>
            <TabsTrigger value="plan">Plan</TabsTrigger>
            <TabsTrigger value="target">Target</TabsTrigger>
            <TabsTrigger value="logs">Logs</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="plan" forceMount className="mt-0 data-[state=inactive]:hidden">
          <div className="mx-auto mb-4 flex w-full max-w-screen-2xl flex-row flex-wrap gap-10 md:w-[90%]">
            <div className="grow w-full">
              {!mealPlan && !isCreatingMealPlan && (
                <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
                  <div className="mx-auto w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-6">
                    <Utensils className="w-12 h-12 text-muted-foreground" />
                  </div>
                  <h3 className="text-2xl font-semibold text-foreground mb-3">
                    No Meal Plans Yet
                  </h3>
                  <p className="text-muted-foreground max-w-md mx-auto mb-8">
                    Create your client&apos;s first meal plan to start tracking
                    nutrition and help them achieve their goals.
                  </p>

                  <Button
                    onClick={handleAddNewMealPlan}
                    size="lg"
                    className="px-8"
                  >
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Create Meal Plan
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setTemplateDialogOpen(true)}
                    size="lg"
                    className="mt-4 px-8"
                  >
                    <Link2 className="mr-2 h-5 w-5" />
                    Link A Template
                  </Button>
                </div>
              )}
              {(isCreatingMealPlan || mealPlan) && (
                <MealPlanCreator
                  mealPlan={mealPlan}
                  mealPlanSaved={handleMealPlanSaved}
                  onDuplicated={handleMealPlanDuplicated}
                  hasSavedMealPlans={hasSavedMealPlans}
                  deleteMealPlan={() => setMealPlanDeleteAlert(true)}
                  onTemplateSaved={handleTemplateSaved}
                  hasChanges={hasChanges}
                  setHasChanges={setHasChanges}
                  showLinkedBadge
                  onGoToTargetTab={() => setNutritionTab('target')}
                  isActive={nutritionTab === 'plan'}
                  planSwitcher={
                    <PlanContexMenu
                      plans={
                        mealPlans?.map((p) => ({
                          id: p.id,
                          name: p.name,
                          is_primary: p.is_primary,
                          is_visible: p.is_visible,
                          is_template: p.is_template,
                        })) ?? []
                      }
                      disabled={mealPlans?.length === 0}
                      planType="nutrition"
                      selectedPlanId={mealPlan?.id}
                      onPlanSelected={(id) => {
                        if (id === mealPlan?.id) return
                        // Check if there are unsaved changes
                        if (hasChanges) {
                          setplanAlertOptions({ id, add: false })
                        } else {
                          const plan = mealPlans?.find((p) => p.id === id)
                          setMealPlan(plan ?? null)
                        }
                      }}
                      onNewPlanSelected={() => {
                        // Check if there are unsaved changes
                        if (hasChanges) {
                          setplanAlertOptions({ id: '', add: true })
                        } else {
                          handleAddNewMealPlan()
                        }
                      }}
                      onImportPlanSelected={() => setTemplateDialogOpen(true)}
                      importLabel="Import meal plan"
                    />
                  }
                />
              )}
            </div>
          </div>
        </TabsContent>
        <TabsContent value="target" className="mt-0">
          <ClientNutritionTargets
            clientId={clientId}
            onDirtyChange={setTargetHasChanges}
          />
        </TabsContent>
        <TabsContent value="logs" className="mt-0">
          <ClientNutritionLogs clientId={clientId} />
        </TabsContent>
      </Tabs>
    </>
  )
}

interface SortableMealProps {
  meal: Meal
  index: number
  onNameChange: (index: number, name: string) => void
  onNotesChange: (index: number, notes: string) => void
  onAddFood: (mealId: string) => void
  onDuplicateMeal: (index: number) => void
  onSaveToLibrary: (meal: Meal) => void
  onReplace: (index: number) => void
  onAddNotes: (index: number) => void
  onDelete: (index: number) => void
  onImageClick: (mealId: string) => void
  onFoodServingChange: (
    mealIndex: number,
  ) => (food: Food, value?: number) => void
  onFoodDeleted: (mealIndex: number) => (food: Food) => void
  onFoodReorder: (
    mealIndex: number,
  ) => (fromIndex: number, toIndex: number) => void
  mealPlan?: MealPlan | null
  deletedMeals: string[]
  setDeletedMeals: (deleted: string[]) => void
  meals: Meal[]
  setMeals: (meals: Meal[]) => void
}

const SortableMeal: FunctionComponent<SortableMealProps> = ({
  meal,
  index,
  onNameChange,
  onNotesChange,
  onAddFood,
  onDuplicateMeal,
  onSaveToLibrary,
  onReplace,
  onAddNotes,
  onDelete,
  onImageClick,
  onFoodServingChange,
  onFoodDeleted,
  onFoodReorder,
  mealPlan,
  deletedMeals,
  setDeletedMeals,
  meals,
  setMeals,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: meal.id })

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 9999 : undefined,
  }

  return (
    <div ref={setNodeRef} style={style} className="flex flex-col">
      <Card className="my-5 overflow-hidden">
        <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div
              className="h-7 w-7 rounded-md p-1.5 text-muted-foreground cursor-grab touch-none active:cursor-grabbing"
              aria-label="Reorder meal"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-4 w-4" />
            </div>
            <button
              type="button"
              onClick={() => onImageClick(meal.id)}
              className="h-12 w-12 rounded-md border border-dashed overflow-hidden flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/40 transition"
              aria-label="Edit meal image"
            >
              <MealImage
                imageUrl={meal.image?.url}
                className="h-full w-full"
                iconClassName="h-4 w-4"
              />
            </button>
            <Input
              value={meal.name}
              placeholder="New meal"
              onChange={(e) => onNameChange(index, e.target.value)}
              className="min-w-0 flex-1 border-none text-base font-bold"
            />
          </div>
          <div className="flex gap-4">
            <ButtonGroup>
              <Button variant={'outline'} onClick={() => onAddFood(meal.id)}>
                <PlusIcon className="h-4 w-4 shrink-0 sm:hidden" />
                <span className="hidden sm:inline">Add Food</span>
                <PlusIcon className="hidden h-4 w-4 shrink-0 opacity-50 sm:ml-2 sm:inline" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant={'outline'}>
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onSaveToLibrary(meal)}>
                    <Upload className="w-4 h-4 mr-2" />
                    Save to Library
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onReplace(index)}>
                    <ArrowRightLeft className="mr-2 h-4 w-4" />
                    Replace
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onDuplicateMeal(index)}>
                    <Copy className="mr-2 h-4 w-4" />
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={!!meal.notes}
                    onClick={() => onAddNotes(index)}
                  >
                    <NotebookText className="mr-2 h-4 w-4" />
                    Add Notes
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDelete(index)}
                    variant={'destructive'}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </ButtonGroup>
          </div>
        </div>
        {meal.notes !== null && meal.notes !== undefined && (
          <div className="">
            <Textarea
              placeholder="Write your notes here..."
              value={meal.notes}
              onChange={(e) => onNotesChange(index, e.target.value)}
              className="min-h-[88px] resize-none rounded-none border-0 bg-transparent px-4 py-3 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>
        )}
        <MealInfoTable
          foods={meal.foods}
          onFoodServingChange={onFoodServingChange(index)}
          onFoodDeleted={onFoodDeleted(index)}
          onFoodReorder={onFoodReorder(index)}
          roundedTop={false}
        />
      </Card>
    </div>
  )
}

interface MealPlanCreatorProps {
  mealPlan: MealPlan | null
  mealPlanSaved: (
    mealPlan: MealPlan,
    options?: { invalidate?: boolean },
  ) => void
  onDuplicated?: (mealPlan: MealPlan) => void
  hasSavedMealPlans: boolean
  deleteMealPlan: () => void
  onTemplateSaved?: (template: SavedTemplate) => void
  planSwitcher: ReactElement
  hasChanges: boolean
  setHasChanges: (hasChanges: boolean) => void
  showLinkedBadge?: boolean
  onGoToTargetTab?: () => void
  isActive?: boolean
}

const TARGET_CAL_DIFF = 50
const TARGET_MACRO_DIFF_G = 5

function planDiffersFromTarget(
  plan: { calories: number; protein: number; carb: number; fat: number },
  target: { calories: number; protein_g: number; carb_g: number; fat_g: number },
): boolean {
  return (
    Math.abs(plan.calories - target.calories) >= TARGET_CAL_DIFF ||
    Math.abs(plan.protein - target.protein_g) >= TARGET_MACRO_DIFF_G ||
    Math.abs(plan.carb - target.carb_g) >= TARGET_MACRO_DIFF_G ||
    Math.abs(plan.fat - target.fat_g) >= TARGET_MACRO_DIFF_G
  )
}

const MealPlanCreator: FunctionComponent<MealPlanCreatorProps> = ({
  mealPlan,
  mealPlanSaved,
  onDuplicated,
  hasSavedMealPlans,
  deleteMealPlan,
  onTemplateSaved,
  planSwitcher,
  hasChanges,
  setHasChanges,
  showLinkedBadge = false,
  onGoToTargetTab,
  isActive = true,
}) => {
  const [toggleValues, setToggleValues] = useState<string[]>(() => {
    const isVisible = mealPlan?.is_visible ?? true
    const isPrimary =
      ((mealPlan?.is_primary ?? false) ||
        ((!mealPlan?.id || mealPlan.id.length <= 2) && !hasSavedMealPlans)) &&
      isVisible

    const values: string[] = []
    if (isPrimary) values.push('primary')
    if (isVisible) values.push('visible')
    return values
  })

  const editorHistory = useEditorHistory(
    toMealEditorDocument({
      mealname: mealPlan?.name ?? '',
      meals: mealPlan?.meals ?? [],
      deletedMeals: [],
      deletedFoods: [],
    }),
  )
  const { meals, mealname, deletedMeals, deletedFoods } = editorHistory.present
  const [loading, setLoading] = useState(false)
  const utils = trpc.useUtils()
  const saveMealPlan = trpc.nutrition.saveMealPlan.useMutation()
  const setNutritionTarget = trpc.nutrition.setTarget.useMutation()
  const setClientPlanStatus = trpc.nutrition.setClientPlanStatus.useMutation()
  const unlinkFromClient = trpc.nutrition.unlinkFromClient.useMutation()
  const [headerStuck, setHeaderStuck] = useState(false)
  const [planStatusLoading, setPlanStatusLoading] = useState(false)
  const [foodSelectOpen, setFoodSelectOpen] = useState<string | undefined>() // Meal id that wants to add food to
  const [mealToSave, setMealToSave] = useState<Meal>()
  const [mealSelectDialogOpen, setMealSelectDialogOpen] = useState(false)
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false)
  const [saveTemplateName, setSaveTemplateName] = useState('')
  const [saveTemplateLoading, setSaveTemplateLoading] = useState(false)
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false)
  const [duplicateName, setDuplicateName] = useState('')
  const [duplicateLoading, setDuplicateLoading] = useState(false)
  const [historyOpen, setHistoryOpen] = useVersionHistoryOpen()
  const isPreviewing = useVersionHistoryPreview()
  const [mealIndexToReplace, setMealIndexToReplace] = useState<number>()
  const [imageDialogMealId, setImageDialogMealId] = useState<string | null>(
    null,
  )
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false)
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null)
  const [pendingImagePreviewUrl, setPendingImagePreviewUrl] = useState<
    string | null
  >(null)
  const [imageDialogLoading, setImageDialogLoading] = useState(false)
  const [imageDialogAction, setImageDialogAction] = useState<
    'add' | 'clear' | null
  >(null)
  const [pendingImageUploads, setPendingImageUploads] = useState<string[]>([])
  const pendingImageUploadsRef = useRef<string[]>([])
  const mealsRef = useRef<Meal[]>(meals)
  const isLinkedPlan = Boolean(mealPlan?.is_template)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
  )
  const [active, setActive] = useState<Active>()

  const items = useMemo(() => meals.map(({ id }) => id), [meals])
  const planIdRef = useRef(mealPlan?.id)

  const setMeals = useCallback(
    (
      next: Meal[] | ((prev: Meal[]) => Meal[]),
      options?: EditorCommitOptions,
    ) => {
      editorHistory.commitDoc(
        (prev) => ({
          ...prev,
          meals: typeof next === 'function' ? next(prev.meals) : next,
        }),
        options,
      )
    },
    [editorHistory.commitDoc],
  )

  const setMealname = useCallback(
    (value: string, options?: EditorCommitOptions) => {
      editorHistory.commitDoc(
        (prev) => ({ ...prev, mealname: value }),
        options,
      )
    },
    [editorHistory.commitDoc],
  )

  useEffect(() => {
    setHasChanges(editorHistory.hasChanges)
  }, [editorHistory.hasChanges, setHasChanges])

  useEffect(() => {
    mealsRef.current = meals
  }, [meals])

  useEffect(() => {
    pendingImageUploadsRef.current = pendingImageUploads
  }, [pendingImageUploads])
  const selectedMealIndex = useMemo(() => {
    if (!imageDialogMealId) return -1
    return meals.findIndex((meal) => meal.id === imageDialogMealId)
  }, [imageDialogMealId, meals])
  const selectedMeal = selectedMealIndex >= 0 ? meals[selectedMealIndex] : null
  const existingMealImageUrl = selectedMeal?.image?.url ?? undefined
  const dialogPreviewUrl =
    pendingImagePreviewUrl ?? existingMealImageUrl ?? undefined

  const onItemMoved = useCallback(
    (event: DragEndEvent) => {
      document.body.style.removeProperty('cursor')
      setActive(undefined)

      const { active, over } = event
      if (active?.id == null || over?.id == null) {
        return
      }

      if (active.id !== over.id) {
        const oldIndex = meals.findIndex((item) => item.id === active.id)
        const newIndex = meals.findIndex((item) => item.id === over.id)

        if (oldIndex !== -1 && newIndex !== -1) {
          setMeals((prevMeals) => arrayMove(prevMeals, oldIndex, newIndex))
        }
      }
    },
    [meals, setHasChanges],
  )

  const handleDragStart = (event: DragStartEvent) => {
    setActive(event.active)
    document.body.style.setProperty('cursor', 'grabbing', 'important')
  }

  useEffect(() => {
    const isVisible = mealPlan?.is_visible ?? true
    const shouldAutoPrimary =
      (!mealPlan?.id || mealPlan.id.length <= 2) && !hasSavedMealPlans
    const isPrimary =
      ((mealPlan?.is_primary ?? false) || shouldAutoPrimary) && isVisible
    const nextValues: string[] = []
    if (isPrimary) nextValues.push('primary')
    if (isVisible) nextValues.push('visible')
    setToggleValues(nextValues)

    if (planIdRef.current === mealPlan?.id) {
      return
    }
    planIdRef.current = mealPlan?.id
    editorHistory.reset(
      toMealEditorDocument({
        mealname: mealPlan?.name ?? '',
        meals: mealPlan?.meals ?? [],
        deletedMeals: [],
        deletedFoods: [],
      }),
    )
  }, [hasSavedMealPlans, mealPlan, editorHistory.reset])

  useEffect(() => {
    if (imageDialogMealId) {
      return
    }

    if (pendingImagePreviewUrl) {
      URL.revokeObjectURL(pendingImagePreviewUrl)
    }
    setPendingImageFile(null)
    setPendingImagePreviewUrl(null)
  }, [imageDialogMealId, pendingImagePreviewUrl])

  const cleanupPendingMealImages = useCallback(async (nextMeals?: Meal[]) => {
    const pending = pendingImageUploadsRef.current
    if (!pending.length) {
      return
    }
    const usedPaths = new Set(
      (nextMeals ?? mealsRef.current)
        .map((meal) => meal.image_path)
        .filter((path): path is string => Boolean(path)),
    )
    const toRemove = pending.filter((path) => !usedPaths.has(path))
    if (!toRemove.length) {
      return
    }
    const { error } = await supabase.storage
      .from(MEAL_IMAGE_BUCKET)
      .remove(toRemove)
    if (error) {
      console.error('Error cleaning up meal images', error)
      return
    }
    setPendingImageUploads((prev) =>
      prev.filter((path) => !toRemove.includes(path)),
    )
  }, [])

  useEffect(() => {
    return () => {
      void cleanupPendingMealImages()
    }
  }, [cleanupPendingMealImages])

  const updateMealPlanStatus = async (updates: {
    isPrimary?: boolean
    isVisible?: boolean
  }) => {
    if (!mealPlan?.id || mealPlan.id.length <= 2) {
      return { error: new Error('Meal plan not saved yet') }
    }
    setPlanStatusLoading(true)
    try {
      await setClientPlanStatus.mutateAsync({
        clientId,
        planId: mealPlan.id,
        ...updates,
      })
      return { error: null }
    } catch (error) {
      return { error: error instanceof Error ? error : new Error('Failed') }
    } finally {
      setPlanStatusLoading(false)
    }
  }

  const handleSetPrimary = async () => {
    if (!mealPlan?.id || mealPlan.id.length <= 2) return
    const { error } = await updateMealPlanStatus({
      isPrimary: true,
      isVisible: true,
    })
    if (error) {
      errorToast(error)
      return
    }
    setToggleValues(['primary', 'visible'])
    if (mealPlan) {
      mealPlanSaved(
        { ...mealPlan, is_primary: true, is_visible: true },
        { invalidate: false },
      )
    }
    toast.success('Meal plan set as primary')
  }

  const handleToggleVisible = async () => {
    if (!mealPlan?.id || mealPlan.id.length <= 2) return
    const nextVisible = !toggleValues.includes('visible')
    const { error } = await updateMealPlanStatus(
      nextVisible
        ? { isVisible: true }
        : { isVisible: false, isPrimary: false },
    )
    if (error) {
      errorToast(error)
      return
    }
    setToggleValues((prev) => {
      const nextValues = nextVisible
        ? [...prev.filter((v) => v !== 'visible'), 'visible']
        : prev.filter((v) => v !== 'visible' && v !== 'primary')
      return Array.from(new Set(nextValues))
    })
    if (mealPlan) {
      mealPlanSaved(
        {
          ...mealPlan,
          is_visible: nextVisible,
          is_primary: nextVisible ? mealPlan.is_primary : false,
        },
        { invalidate: false },
      )
    }
    toast.success(nextVisible ? 'Meal plan is now visible' : 'Meal plan hidden')
  }

  const bottomRef = useRef<HTMLDivElement>(null)
  const headerSentinelRef = useRef<HTMLDivElement>(null)
  const mainScrollable = useMainScrollableElement()

  const { clientId } = Route.useParams()

  const org = useOrg()

  const validateMealImageFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload a valid image file.')
      return false
    }
    if (file.size > MEAL_IMAGE_MAX_BYTES) {
      toast.error('Image must be 10MB or smaller.')
      return false
    }
    return true
  }

  const setPendingMealImage = (file: File) => {
    if (!validateMealImageFile(file)) {
      return
    }
    if (pendingImagePreviewUrl) {
      URL.revokeObjectURL(pendingImagePreviewUrl)
    }
    setPendingImageFile(file)
    setPendingImagePreviewUrl(URL.createObjectURL(file))
  }

  const {
    getRootProps: getMealImageRootProps,
    getInputProps: getMealImageInputProps,
    isDragActive: isMealImageDragActive,
    isDragReject: isMealImageDragReject,
  } = useDropzone({
    accept: { 'image/*': [] },
    maxSize: MEAL_IMAGE_MAX_BYTES,
    maxFiles: 1,
    multiple: false,
    disabled: imageDialogLoading,
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        const message =
          fileRejections[0].errors[0]?.message ??
          'Please upload a valid image file.'
        toast.error(message)
        return
      }
      const file = acceptedFiles[0]
      if (file) {
        setPendingMealImage(file)
      }
    },
  })

  const handleApplyMealImage = async () => {
    if (!pendingImageFile || !selectedMeal || !org?.id) {
      return
    }

    setImageDialogAction('add')
    setImageDialogLoading(true)
    const newImagePath = buildMealImagePath(org.id, selectedMeal.id)
    const { error: uploadError } = await supabase.storage
      .from(MEAL_IMAGE_BUCKET)
      .upload(newImagePath, pendingImageFile, {
        cacheControl: '3600',
        upsert: true,
        contentType: pendingImageFile.type,
      })

    if (uploadError) {
      console.error('Error uploading meal image', uploadError)
      errorToast(uploadError)
      setImageDialogLoading(false)
      setImageDialogAction(null)
      return
    }

    const nextMeals = [...meals]
    nextMeals[selectedMealIndex] = {
      ...selectedMeal,
      image_path: newImagePath,
      image: null,
    }
    setMeals(nextMeals)
    setPendingImageUploads((prev) =>
      prev.includes(newImagePath) ? prev : [...prev, newImagePath],
    )
    setHasChanges(true)

    if (pendingImagePreviewUrl) {
      URL.revokeObjectURL(pendingImagePreviewUrl)
    }
    setPendingImageFile(null)
    setPendingImagePreviewUrl(null)
    setImageDialogMealId(null)
    setImageDialogLoading(false)
    setImageDialogAction(null)
  }

  const handleClearMealImage = async () => {
    if (!selectedMeal) {
      return
    }

    setImageDialogAction('clear')
    setImageDialogLoading(true)
    if (selectedMeal.image_path) {
      const { error: removeError } = await supabase.storage
        .from(MEAL_IMAGE_BUCKET)
        .remove([selectedMeal.image_path])
      if (removeError) {
        console.error('Error removing meal image', removeError)
        errorToast(removeError)
        setImageDialogLoading(false)
        setImageDialogAction(null)
        return
      }
      setPendingImageUploads((prev) =>
        prev.filter((path) => path !== selectedMeal.image_path),
      )
    }

    const nextMeals = [...meals]
    nextMeals[selectedMealIndex] = {
      ...selectedMeal,
      image_path: null,
      image: null,
    }
    setMeals(nextMeals)
    setHasChanges(true)

    if (pendingImagePreviewUrl) {
      URL.revokeObjectURL(pendingImagePreviewUrl)
    }
    setPendingImageFile(null)
    setPendingImagePreviewUrl(null)
    setImageDialogMealId(null)
    setImageDialogLoading(false)
    setImageDialogAction(null)
  }

  const onMealSelected = (meal: Meal) => {
    const normalizedNotes =
      typeof meal.notes === 'string' && meal.notes.trim().length > 0
        ? meal.notes
        : null
    if (mealIndexToReplace !== undefined) {
      // Replace meal
      const newMeal = {
        ...meal,
        notes: normalizedNotes,
        id: meals[mealIndexToReplace].id,
      }
      const replacedMealId = meals[mealIndexToReplace].id
      const deleted = meals[mealIndexToReplace].foods.map((f) => ({
        foodId: f.id,
        mealId: replacedMealId,
      }))
      editorHistory.commitDoc((prev) => ({
        ...prev,
        deletedFoods: [...prev.deletedFoods, ...deleted],
        meals: prev.meals.map((m, i) =>
          i === mealIndexToReplace ? newMeal : m,
        ),
      }))
      setMealIndexToReplace(undefined)
    } else {
      // New ID to duplicate meal from template and add to bottom
      const newMeal = { ...meal, notes: normalizedNotes, id: uuidv4() }
      setMeals([...meals, newMeal])
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
      }, 200)
    }
  }

  const handleFoodServingUpdated =
    (mealIndex: number) => (food: Food, value?: number) => {
      const newMeals = [...meals]
      const newFoods = newMeals[mealIndex].foods.map((f) => {
        if (f.id === food.id) {
          const newFood: Food = {
            ...f,
            serving_size: value,
          }
          return newFood
        } else {
          return f
        }
      })

      newMeals[mealIndex] = {
        ...meals[mealIndex],
        foods: newFoods,
      }
      setMeals(newMeals, { coalesceKey: `food:${food.id}:serving` })
    }

  const handleFoodDeleted = (mealIndex: number) => (food: Food) => {
    const newMeals = [...meals]
    const newFoods = newMeals[mealIndex].foods.filter((f) => f.id !== food.id)
    newMeals[mealIndex] = {
      ...meals[mealIndex],
      foods: newFoods,
    }
    if (mealPlan?.id) {
      editorHistory.commitDoc((prev) => ({
        ...prev,
        meals: newMeals,
        deletedFoods: [
          ...prev.deletedFoods,
          { foodId: food.id, mealId: prev.meals[mealIndex].id },
        ],
      }))
      return
    }
    setMeals(newMeals)
  }

  const handleFoodReordered =
    (mealIndex: number) => (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) {
        return
      }

      const nextMeals = [...meals]
      nextMeals[mealIndex] = {
        ...nextMeals[mealIndex],
        foods: arrayMove(nextMeals[mealIndex].foods, fromIndex, toIndex),
      }
      setMeals(nextMeals)
      setHasChanges(true)
    }

  const handleDuplicateMeal = async (mealIndex: number) => {
    const mealToDuplicate = meals[mealIndex]
    if (!mealToDuplicate) {
      return
    }

    const duplicatedMealId = uuidv4()
    let duplicatedImagePath: string | null = null

    if (mealToDuplicate.image_path && org?.id) {
      const nextImagePath = buildMealImagePath(org.id, duplicatedMealId)
      const { error } = await supabase.storage
        .from(MEAL_IMAGE_BUCKET)
        .copy(mealToDuplicate.image_path, nextImagePath)

      if (error) {
        console.error('Error copying duplicated meal image', error)
        toast.error('Meal duplicated without its image.')
      } else {
        duplicatedImagePath = nextImagePath
        setPendingImageUploads((prev) =>
          prev.includes(nextImagePath) ? prev : [...prev, nextImagePath],
        )
      }
    }

    const duplicatedMeal: Meal = {
      ...mealToDuplicate,
      id: duplicatedMealId,
      name: `${mealToDuplicate.name} (copy)`,
      image_path: duplicatedImagePath,
      image: null,
      foods: mealToDuplicate.foods.map((food) => ({ ...food })),
    }

    const nextMeals = [...meals, duplicatedMeal]
    setMeals(nextMeals)
    setHasChanges(true)
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 200)
  }

  const handleOpenSaveTemplateDialog = () => {
    setSaveTemplateName(mealname.trim() || 'Meal Plan Template')
    setSaveTemplateDialogOpen(true)
  }

  const handleOpenDuplicateDialog = () => {
    setDuplicateName(`${mealname.trim() || 'Meal Plan'} (copy)`)
    setDuplicateDialogOpen(true)
  }

  const handleSaveAsTemplate = async () => {
    const trimmedName = saveTemplateName.trim()

    if (!org?.id) {
      return
    }

    if (!trimmedName) {
      toast.error('Please enter a template name.')
      return
    }

    if (!meals.length) {
      toast.error('Meal plan requires at least one meal.')
      return
    }

    setSaveTemplateLoading(true)

    let imageCopyFailed = false
    const copiedImagePaths: string[] = []
    const templateMeals: Meal[] = await Promise.all(
      meals.map(async (meal, index) => {
        const newMealId = uuidv4()
        let imagePath: string | null = null

        if (meal.image_path) {
          const nextImagePath = buildMealImagePath(org.id, newMealId)
          const { error } = await supabase.storage
            .from(MEAL_IMAGE_BUCKET)
            .copy(meal.image_path, nextImagePath)

          if (error) {
            console.error('Error copying template meal image', error)
            imageCopyFailed = true
          } else {
            imagePath = nextImagePath
            copiedImagePaths.push(nextImagePath)
          }
        }

        return {
          ...meal,
          id: newMealId,
          notes: meal.notes?.length ? meal.notes : null,
          image_path: imagePath,
          image: null,
          meal_number: index,
          foods: meal.foods.map((food) => ({ ...food })),
        }
      }),
    )

    const params = {
      org_id: org.id,
      meal_plan_id: null,
      name: trimmedName,
      client_id: null,
      meals: templateMeals.map((meal, index) => ({
        id: meal.id,
        name: meal.name,
        notes: meal.notes,
        image_path: meal.image_path ?? null,
        meal_number: index,
        foods: meal.foods.map((food, foodIndex) => ({
          id: food.id,
          serving_size: food.serving_size,
          sort_order: foodIndex,
        })),
      })),
      deleted_meals: [],
      deleted_foods: [],
      is_template: true,
    }

    const { error, data } = await (async () => {
      try {
        const saved = await saveMealPlan.mutateAsync({ params })
        return { error: null, data: saved.id }
      } catch (error) {
        return { error, data: null }
      }
    })()

    setSaveTemplateLoading(false)

    if (error || !data) {
      if (copiedImagePaths.length) {
        const { error: cleanupError } = await supabase.storage
          .from(MEAL_IMAGE_BUCKET)
          .remove(copiedImagePaths)
        if (cleanupError) {
          console.error(
            'Error cleaning up copied template meal images',
            cleanupError,
          )
        }
      }
      console.error(error)
      errorToast(error instanceof Error ? error : null)
      return
    }

    onTemplateSaved?.({
      id: data,
      name: trimmedName,
      org_id: org.id,
      meals: templateMeals,
    })
    setSaveTemplateDialogOpen(false)
    toast.success(
      imageCopyFailed
        ? 'Template saved. Some meal images could not be copied.'
        : 'Meal plan saved as template',
    )
  }

  const handleDuplicatePlan = async () => {
    const trimmedName = duplicateName.trim()

    if (!org?.id) {
      return
    }

    if (!trimmedName) {
      toast.error('Please enter a meal plan name.')
      return
    }

    setDuplicateLoading(true)

    try {
      const { duplicatedMeals, imageCopyFailed, mealPlanId } =
        await duplicateMealPlan({
          orgId: org.id,
          name: trimmedName,
          meals,
          isTemplate: false,
          clientId,
          isVisible: true,
          isPrimary: false,
          saveMealPlan: async (params) =>
            (await saveMealPlan.mutateAsync({ params })).id,
        })

      const duplicatedPlan: MealPlan = {
        id: mealPlanId,
        name: trimmedName,
        is_primary: false,
        is_visible: true,
        is_template: false,
        meals: duplicatedMeals,
      }

      onDuplicated?.(duplicatedPlan)
      if (!onDuplicated) {
        mealPlanSaved(duplicatedPlan)
      }
      setHasChanges(false)
      setDuplicateDialogOpen(false)
      toast.success(
        imageCopyFailed
          ? 'Meal plan duplicated. Some meal images could not be copied.'
          : 'Meal plan duplicated',
      )
    } catch (error) {
      console.error(error)
      errorToast(
        error instanceof Error ? error : new Error('Failed to duplicate plan'),
      )
    } finally {
      setDuplicateLoading(false)
    }
  }

  const totalFromFoodField = useCallback(
    (field: keyof Food) => FoodsFieldTotal(field, meals),
    [meals],
  )

  const { data: targets } = trpc.nutrition.listTargets.useQuery({ clientId })

  const targetWeeklyAvg = useMemo(() => {
    const target = (targets ?? []).find((row) => row.effective_to == null)
    if (!target?.client_nutrition_target_days?.length) return null

    const days = target.client_nutrition_target_days
    if (target.schedule_type === 'weekly') {
      const byDow = Array.from({ length: 7 }, (_, dow) => {
        const found = days.find((d) => d.day_of_week === dow)
        return (
          found ?? {
            day_of_week: dow,
            calories: 0,
            protein_g: 0,
            carb_g: 0,
            fat_g: 0,
            protein_pct: null,
            carb_pct: null,
            fat_pct: null,
          }
        )
      })
      return averageDays(byDow)
    }

    return averageDays(days)
  }, [targets])

  const [targetMismatchOpen, setTargetMismatchOpen] = useState(false)
  const [updatingTarget, setUpdatingTarget] = useState(false)
  const [editTargetsOpen, setEditTargetsOpen] = useState(false)
  const [pendingPlanTotals, setPendingPlanTotals] = useState<{
    calories: number
    protein: number
    carb: number
    fat: number
  } | null>(null)

  const handleSaveMealPlan = async (options?: { unlinkTemplate?: boolean }) => {
    const shouldUnlinkTemplate = options?.unlinkTemplate ?? false
    if (!mealname.length) {
      toast.error('Please enter a name for the meal plan')
      return
    }

    if (!meals.length) {
      toast.error('Meal plan requires at least one meal')
      return
    }

    setLoading(true)

    const mealIdMap = shouldUnlinkTemplate
      ? new Map(meals.map((meal) => [meal.id, uuidv4()]))
      : null
    const mealsForSave = meals.map((meal) => ({
      ...meal,
      id: mealIdMap?.get(meal.id) ?? meal.id,
    }))

    const m = mealsForSave.map((meal, index) => {
      const foods = meal.foods.map((food, foodIndex) => {
        return {
          id: food.id,
          serving_size: food.serving_size,
          sort_order: foodIndex,
        }
      })
      const notes = meal.notes?.length ? meal.notes : null
      return {
        id: meal.id,
        name: meal.name,
        notes,
        image_path: meal.image_path ?? null,
        meal_number: index,
        foods,
      }
    })

    const deleted = deletedMeals.map((id) => id)

    let id = mealPlan?.id
    if (!id || id.length <= 2) {
      id = undefined
    }
    if (shouldUnlinkTemplate) {
      id = undefined
    }

    const nextIsVisible = toggleValues.includes('visible')
    const isNewPlan = !mealPlan?.id || mealPlan.id.length <= 2
    const shouldAutoPrimary = isNewPlan && !hasSavedMealPlans
    const nextIsPrimary =
      (toggleValues.includes('primary') || shouldAutoPrimary) && nextIsVisible

    const params = {
      org_id: org?.id,
      meal_plan_id: id ?? null,
      name: mealname,
      client_id: clientId,
      is_visible: nextIsVisible,
      is_primary: nextIsPrimary,
      meals: m,
      deleted_meals: shouldUnlinkTemplate ? [] : deleted,
      deleted_foods: shouldUnlinkTemplate ? [] : deletedFoods,
    }

    let data: string | undefined
    try {
      const saved = await saveMealPlan.mutateAsync({ params })
      data = saved.id
    } catch (error) {
      console.error(error)
      errorToast(error instanceof Error ? error : null)
      setLoading(false)
      return
    }
    setLoading(false)
    if (!data) {
      errorToast(null, 'Failed to save meal plan')
      return
    }

    const savedDocument = toMealEditorDocument({
      mealname,
      meals: mealsForSave,
      deletedMeals: [],
      deletedFoods: [],
    })
    editorHistory.replacePresent(savedDocument)
    editorHistory.rebaseline(savedDocument)

    if (data) {
      const id = data
      toast.success('Meal plan saved')
      await cleanupPendingMealImages(mealsForSave)
      setPendingImageUploads([])
      const newMealPlan: MealPlan = {
        is_visible: nextIsVisible,
        is_primary: nextIsPrimary,
        id,
        name: mealname,
        meals: mealsForSave,
        is_template: false,
      }
      if (shouldUnlinkTemplate && mealPlan?.id) {
        try {
          await unlinkFromClient.mutateAsync({
            clientId,
            planId: mealPlan.id,
          })
        } catch (unlinkError) {
          console.error('Error unlinking template meal plan', unlinkError)
          errorToast(unlinkError instanceof Error ? unlinkError : null)
        }
      }
      mealPlanSaved(newMealPlan)
      // Reset hasChanges state after saving
      setHasChanges(false)

      const planTotals = {
        calories: FoodsFieldTotal('calories', mealsForSave),
        protein: FoodsFieldTotal('protein', mealsForSave),
        carb: FoodsFieldTotal('carb', mealsForSave),
        fat: FoodsFieldTotal('fat', mealsForSave),
      }
      if (
        targetWeeklyAvg != null &&
        planDiffersFromTarget(planTotals, targetWeeklyAvg)
      ) {
        setPendingPlanTotals(planTotals)
        setTargetMismatchOpen(true)
      }
    }
  }

  const handleUpdateTargetFromPlan = async () => {
    if (!pendingPlanTotals) return
    setUpdatingTarget(true)
    let saved: Record<string, unknown>
    try {
      saved = await setNutritionTarget.mutateAsync({
        params: {
          client_id: clientId,
          schedule_type: 'uniform',
          input_mode: 'grams',
          effective_from: luxon.DateTime.utc().toISODate(),
          notes: null,
          days: [
            {
              day_of_week: null,
              calories: Math.round(pendingPlanTotals.calories),
              protein_g: Math.round(pendingPlanTotals.protein * 10) / 10,
              carb_g: Math.round(pendingPlanTotals.carb * 10) / 10,
              fat_g: Math.round(pendingPlanTotals.fat * 10) / 10,
              protein_pct: null,
              carb_pct: null,
              fat_pct: null,
            },
          ],
        },
      })
    } catch (error) {
      errorToast(
        error instanceof Error ? error : null,
        'Failed to update nutrition target',
      )
      setUpdatingTarget(false)
      return
    }
    const savedRow = savedTargetToRow(saved)
    if (savedRow) {
      patchKeyedListCache(utils.nutrition.listTargets, { clientId }, (old) =>
        applySavedTarget(old as NutritionTargetRow[], savedRow),
      )
    } else {
      void utils.nutrition.listTargets.invalidate({ clientId })
    }
    setUpdatingTarget(false)
    toast.success('Nutrition target updated from meal plan')
    setTargetMismatchOpen(false)
    setPendingPlanTotals(null)
  }

  const handleCreateNewMeal = () => {
    const newMeal: Meal = {
      id: uuidv4(),
      name: 'New Meal',
      meal_number: meals.length,
      foods: [],
    }
    setMeals([...meals, newMeal])
    setHasChanges(true)
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 200)
  }

  useEffect(() => {
    if (!isActive) {
      setHeaderStuck(false)
      return
    }
    const sentinel = headerSentinelRef.current
    if (!sentinel || !mainScrollable) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        setHeaderStuck(!entry.isIntersecting)
      },
      { root: mainScrollable, threshold: 0 },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [isActive, mainScrollable])

  return (
    <VersionHistorySheet
      kind="meal"
      planId={
        mealPlan?.id && mealPlan.id.length > 2 ? mealPlan.id : null
      }
      open={historyOpen}
      onOpenChange={setHistoryOpen}
      hasChanges={hasChanges}
      liveSnapshot={liveMealSnapshotFromEditor({
        planId: mealPlan?.id,
        name: mealname,
        meals,
      })}
      onApplyRevision={(snapshot: MealRevisionSnapshot, options) => {
        const next = mealEditorStateFromSnapshot(snapshot, meals, {
          meals: deletedMeals,
          foods: deletedFoods,
        })
        editorHistory.reset(
          toMealEditorDocument({
            mealname: next.name,
            meals: next.meals,
            deletedMeals: next.deletedMeals,
            deletedFoods: next.deletedFoods,
          }),
          { markDirty: options.markDirty },
        )
      }}
    >
    <>
      <AlertDialog
        open={targetMismatchOpen}
        onOpenChange={(open) => {
          setTargetMismatchOpen(open)
          if (!open) setPendingPlanTotals(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Meal plan differs from target
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  This meal plan&apos;s totals don&apos;t match the client&apos;s
                  current nutrition target. Update the target to match the plan,
                  or review it on the Target tab.
                </p>
                {pendingPlanTotals && targetWeeklyAvg && (
                  <div className="grid grid-cols-2 gap-3 rounded-md border bg-muted/40 p-3 text-foreground">
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Meal plan
                      </div>
                      <div className="mt-1.5 space-y-1.5">
                        <div className="flex items-center gap-1.5">
                          <Flame className="h-3.5 w-3.5 text-orange-500" />
                          <span className="font-semibold">
                            {Math.round(pendingPlanTotals.calories)}
                          </span>
                          <span className="text-muted-foreground">cal</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Beef
                            className="h-3.5 w-3.5"
                            style={{ color: MACRO_COLORS.protein }}
                          />
                          <span className="font-semibold">
                            {Math.round(pendingPlanTotals.protein)}g
                          </span>
                          <span className="text-muted-foreground">protein</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Wheat
                            className="h-3.5 w-3.5"
                            style={{ color: MACRO_COLORS.carbs }}
                          />
                          <span className="font-semibold">
                            {Math.round(pendingPlanTotals.carb)}g
                          </span>
                          <span className="text-muted-foreground">carbs</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Droplets
                            className="h-3.5 w-3.5"
                            style={{ color: MACRO_COLORS.fat }}
                          />
                          <span className="font-semibold">
                            {Math.round(pendingPlanTotals.fat)}g
                          </span>
                          <span className="text-muted-foreground">fat</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Current target
                      </div>
                      <div className="mt-1.5 space-y-1.5">
                        <div className="flex items-center gap-1.5">
                          <Flame className="h-3.5 w-3.5 text-orange-500" />
                          <span className="font-semibold">
                            {Math.round(targetWeeklyAvg.calories)}
                          </span>
                          <span className="text-muted-foreground">cal</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Beef
                            className="h-3.5 w-3.5"
                            style={{ color: MACRO_COLORS.protein }}
                          />
                          <span className="font-semibold">
                            {Math.round(targetWeeklyAvg.protein_g)}g
                          </span>
                          <span className="text-muted-foreground">protein</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Wheat
                            className="h-3.5 w-3.5"
                            style={{ color: MACRO_COLORS.carbs }}
                          />
                          <span className="font-semibold">
                            {Math.round(targetWeeklyAvg.carb_g)}g
                          </span>
                          <span className="text-muted-foreground">carbs</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Droplets
                            className="h-3.5 w-3.5"
                            style={{ color: MACRO_COLORS.fat }}
                          />
                          <span className="font-semibold">
                            {Math.round(targetWeeklyAvg.fat_g)}g
                          </span>
                          <span className="text-muted-foreground">fat</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            <AlertDialogCancel disabled={updatingTarget}>
              Keep target
            </AlertDialogCancel>
            <Button
              type="button"
              variant="outline"
              disabled={updatingTarget}
              onClick={() => {
                setTargetMismatchOpen(false)
                setPendingPlanTotals(null)
                onGoToTargetTab?.()
              }}
            >
              Go to Target tab
            </Button>
            <AlertDialogAction
              disabled={updatingTarget}
              onClick={(e) => {
                e.preventDefault()
                void handleUpdateTargetFromPlan()
              }}
            >
              {updatingTarget ? 'Updating…' : 'Update target'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <UnlinkTemplateDialog
        open={unlinkDialogOpen}
        onCancel={() => setUnlinkDialogOpen(false)}
        onConfirm={() => {
          setUnlinkDialogOpen(false)
          handleSaveMealPlan({ unlinkTemplate: true })
        }}
        description="Saving will unlink from the template and create a new plan."
      />
      <NutritionTargetDialog
        open={editTargetsOpen}
        onOpenChange={setEditTargetsOpen}
        clientId={clientId}
      />
      <FoodSelectDialog
        open={foodSelectOpen != null}
        onOpenChange={(open) => !open && setFoodSelectOpen(undefined)}
        onSelect={(food) => {
          const f = { ...food }
          f.serving_size = food.default_serving_size
          const newMeals = [...meals]
          newMeals.forEach((m) => {
            if (m.id === foodSelectOpen) {
              m.foods.push(f)
            }
          })
          setMeals(newMeals)
          setHasChanges(true)
          setFoodSelectOpen(undefined)
        }}
      />
      <MealDialog
        open={mealToSave !== undefined}
        meal={mealToSave}
        onOpenChange={(open) => {
          if (!open) {
            setMealToSave(undefined)
          }
        }}
        templateFromMeal={true}
        title="Save Meal to Library"
      />
      <MealSelectDialog
        open={mealSelectDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setMealSelectDialogOpen(false)
          }
        }}
        onSelect={onMealSelected}
      />
      <Dialog
        open={saveTemplateDialogOpen}
        onOpenChange={(open) => {
          setSaveTemplateDialogOpen(open)
          if (!open) {
            setSaveTemplateLoading(false)
          }
        }}
        analyticsName="client_nutrition_save_template_dialog"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save meal plan as template</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm font-medium">Template name</p>
            <Input
              value={saveTemplateName}
              onChange={(event) => setSaveTemplateName(event.target.value)}
              placeholder="Meal Plan Template"
              disabled={saveTemplateLoading}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSaveTemplateDialogOpen(false)}
              disabled={saveTemplateLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAsTemplate}
              disabled={saveTemplateLoading}
            >
              Save as Template
              {saveTemplateLoading && <LoadingSpinner className="ml-2" />}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DuplicateMealPlanDialog
        open={duplicateDialogOpen}
        onOpenChange={(open) => {
          setDuplicateDialogOpen(open)
          if (!open) setDuplicateLoading(false)
        }}
        name={duplicateName}
        onNameChange={setDuplicateName}
        onConfirm={handleDuplicatePlan}
        loading={duplicateLoading}
      />
      <Dialog
        open={imageDialogMealId != null}
        onOpenChange={(open) => {
          if (!open) {
            setImageDialogMealId(null)
          }
        }}
        analyticsName="client_nutrition_meal_image_dialog"
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Meal Image</DialogTitle>
          </DialogHeader>
          <div
            {...getMealImageRootProps({
              className: `rounded-md border-2 border-dashed p-6 text-center transition ${
                isMealImageDragActive
                  ? 'border-primary bg-primary/10'
                  : 'border-muted'
              } ${isMealImageDragReject ? 'border-destructive bg-destructive/10' : ''} ${
                imageDialogLoading ? 'pointer-events-none opacity-60' : ''
              }`,
            })}
          >
            <input
              {...getMealImageInputProps({ disabled: imageDialogLoading })}
            />
            {dialogPreviewUrl ? (
              <img
                src={dialogPreviewUrl}
                alt="Meal preview"
                className="mx-auto h-48 w-48 rounded-md object-cover"
              />
            ) : (
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Drag an image here, or click to upload.</p>
                <p>Max size: 10MB</p>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleClearMealImage}
              disabled={
                imageDialogLoading ||
                (!selectedMeal?.image_path && !pendingImagePreviewUrl)
              }
            >
              Clear image
              {imageDialogAction === 'clear' && (
                <LoadingSpinner className="ml-2" />
              )}
            </Button>
            <Button
              type="button"
              onClick={handleApplyMealImage}
              disabled={imageDialogLoading || !pendingImageFile}
            >
              Add image
              {imageDialogAction === 'add' && (
                <LoadingSpinner className="ml-2" />
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div>
        <div
          ref={headerSentinelRef}
          className="h-px w-full"
          aria-hidden
        />
        <div
          className={cn(
            'sticky top-0 z-40 border-b border-transparent bg-background transition-[border-color,background-color,box-shadow] duration-200',
            headerStuck &&
              'border-border bg-background/80 shadow-sm backdrop-blur',
          )}
        >
        <div className="mx-auto flex w-full max-w-screen-2xl flex-wrap items-center justify-end gap-2 px-4 py-1 md:justify-between">
          <div className="flex min-w-0 flex-1 flex-row items-center gap-2">
            {planSwitcher}
            <InputFieldEditButton
              value={mealname}
              setValue={(value) => {
                setMealname(value, { coalesceKey: 'plan-name' })
              }}
              disabled={isPreviewing}
              placeholder="New Meal Plan Name"
              className="min-w-0 flex-1 text-2xl font-semibold"
              trailingIcon={
                showLinkedBadge && mealPlan?.is_template ? (
                  <Link2 className="h-4 w-4" />
                ) : undefined
              }
              trailingTooltip={
                showLinkedBadge && mealPlan?.is_template
                  ? 'Linked template'
                  : undefined
              }
            />
            <PlanVersionTag />
          </div>

          <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
            <EditorUndoControls
              canUndo={editorHistory.canUndo}
              canRedo={editorHistory.canRedo}
              disabled={isPreviewing}
              onUndo={() => Boolean(editorHistory.undo())}
              onRedo={() => Boolean(editorHistory.redo())}
            />
            <div className={isPreviewing ? 'block' : 'hidden md:block'}>
              <PlanHistoryButton
                disabled={!mealPlan?.id || mealPlan.id.length <= 2}
                onClick={() => setHistoryOpen(true)}
              />
            </div>
            <ButtonGroup className="shrink-0 flex-nowrap whitespace-nowrap">
              <Button
                onClick={() => {
                  if (mealPlan?.is_template) {
                    setUnlinkDialogOpen(true)
                    return
                  }
                  handleSaveMealPlan()
                }}
                disabled={loading || !hasChanges || isPreviewing}
                variant="outline"
                className="shrink-0"
              >
                Save
                {loading && <LoadingSpinner className="ml-2" />}
              </Button>
              <ButtonGroupSeparator />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="shrink-0">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className={isPreviewing ? 'hidden' : 'md:hidden'}
                    disabled={!mealPlan?.id || mealPlan.id.length <= 2}
                    onClick={() => setHistoryOpen(true)}
                  >
                    <History className="mr-2 h-4 w-4" />
                    History
                  </DropdownMenuItem>
                  <DropdownMenuSeparator
                    className={isPreviewing ? 'hidden' : 'md:hidden'}
                  />
                  <DropdownMenuItem
                    disabled={
                      loading ||
                      planStatusLoading ||
                      !mealPlan?.id ||
                      mealPlan.id.length <= 2 ||
                      !toggleValues.includes('visible') ||
                      toggleValues.includes('primary')
                    }
                    onClick={handleSetPrimary}
                  >
                    {toggleValues.includes('primary') ? (
                      <CheckCheck className="mr-2 h-4 w-4" />
                    ) : (
                      <Check className="mr-2 h-4 w-4" />
                    )}
                    {toggleValues.includes('primary')
                      ? 'Primary'
                      : 'Set as Primary'}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={
                      loading ||
                      planStatusLoading ||
                      !mealPlan?.id ||
                      mealPlan.id.length <= 2
                    }
                    onClick={handleToggleVisible}
                  >
                    {toggleValues.includes('visible') ? (
                      <Eye className="mr-2 h-4 w-4" />
                    ) : (
                      <EyeOff className="mr-2 h-4 w-4" />
                    )}
                    {toggleValues.includes('visible') ? 'Hide' : 'Make Visible'}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={
                      loading || saveTemplateLoading || meals.length === 0
                    }
                    onClick={handleOpenSaveTemplateDialog}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Save as Template
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={duplicateLoading}
                    onClick={handleOpenDuplicateDialog}
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={deleteMealPlan}
                    variant="destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {isLinkedPlan ? 'Remove Plan' : 'Delete Plan'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </ButtonGroup>
          </div>
        </div>
          <div className="mx-auto w-full max-w-screen-2xl px-4 py-1">
            <div className="flex w-full flex-wrap items-center gap-3 sm:flex-nowrap sm:gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-auto shrink-0 sm:px-3 gap-2" disabled={isPreviewing}>
                    <span className="sm:hidden">New</span>
                    <span className="hidden sm:inline">Add New</span>
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={handleCreateNewMeal}>
                    <PlusIcon className="mr-2 h-4 w-4" />
                    Meal
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setMealSelectDialogOpen(true)}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    From Library
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <MealPlanMacroBars
                totals={{
                  calories: totalFromFoodField('calories'),
                  protein: totalFromFoodField('protein'),
                  carb: totalFromFoodField('carb'),
                  fat: totalFromFoodField('fat'),
                }}
                targets={targetWeeklyAvg}
                onEditTargets={
                  isPreviewing ? undefined : () => setEditTargetsOpen(true)
                }
              />
            </div>
          </div>
        </div>

        <div
          className={cn(
            'mx-auto w-full max-w-screen-2xl px-3 md:px-0',
            isPreviewing && 'pointer-events-none',
          )}
        >
          <DndContext
            sensors={sensors}
            modifiers={[restrictToVerticalAxis]}
            measuring={{
              droppable: { strategy: MeasuringStrategy.Always },
            }}
            collisionDetection={closestCenter}
            onDragEnd={onItemMoved}
            onDragStart={handleDragStart}
          >
            <SortableContext items={items}>
              {meals.map((item: Meal, index: number) => (
                <SortableMeal
                  key={item.id}
                  meal={item}
                  index={index}
                  onNameChange={(index, name) => {
                    const newMeals = [...meals]
                    newMeals[index].name = name
                    setMeals(newMeals)
                    setHasChanges(true)
                  }}
                  onNotesChange={(index, notes) => {
                    const newMeals = [...meals]
                    newMeals[index].notes = notes
                    setMeals(newMeals)
                    setHasChanges(true)
                  }}
                  onAddFood={setFoodSelectOpen}
                  onDuplicateMeal={handleDuplicateMeal}
                  onSaveToLibrary={(meal) => setMealToSave({ ...meal })}
                  onReplace={(index) => {
                    setMealIndexToReplace(index)
                    setMealSelectDialogOpen(true)
                  }}
                  onAddNotes={(index) => {
                    const newMeals = [...meals]
                    newMeals[index].notes = ''
                    setMeals(newMeals)
                    setHasChanges(true)
                  }}
                  onImageClick={(mealId) => {
                    setImageDialogMealId(mealId)
                  }}
                  onDelete={(index) => {
                    const newMeals = [...meals]
                    newMeals.splice(index, 1)
                    editorHistory.commitDoc((prev) => ({
                      ...prev,
                      meals: newMeals,
                      deletedMeals: mealPlan?.id
                        ? [...prev.deletedMeals, item.id]
                        : prev.deletedMeals,
                    }))
                    void cleanupPendingMealImages(newMeals)
                  }}
                  onFoodServingChange={handleFoodServingUpdated}
                  onFoodDeleted={handleFoodDeleted}
                  onFoodReorder={handleFoodReordered}
                  mealPlan={mealPlan}
                  deletedMeals={deletedMeals}
                  setDeletedMeals={(deleted) => {
                    editorHistory.commitDoc((prev) => ({
                      ...prev,
                      deletedMeals: deleted,
                    }))
                  }}
                  meals={meals}
                  setMeals={setMeals}
                />
              ))}
            </SortableContext>
          </DndContext>

          <div ref={bottomRef}></div>
        </div>
      </div>
    </>
    </VersionHistorySheet>
  )
}
